import { useApprovePageMutation, useGetTaskCountMutation } from 'api/collaborateApi';
import { Check, Save } from 'lucide-react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'ui';
import Button from 'ui/components/Button';
import { cn } from 'utils/cn';
import { useResponsive } from 'utils/useMediaQuery';
import { getCurrentPageUrlFilter } from '../../store/filtersSlice';
import { getSelectedSiteCollaborateInfo, getSelectedSiteId, getShowTaskCountModal, setShowTaskCountModal } from '../../store/siteInfoSlice';
import { getBrowseMode, getIsBottomBarLoading } from '../../store/uiSlice';
import GodaddyConfirmTaskModal from '../modals/godaddy-confirm-task/godaddy-confirm-task-modal';


type PageStatusType = 'in-progress' | 'needs-review' | 'approved' | null

export const CollaboratorSubmit = () => {
  const { t } = useTranslation()
  const siteId = useSelector(getSelectedSiteId)
  const showTaskCountModal = useSelector(getShowTaskCountModal);
  const itemDetails = useSelector(getSelectedSiteCollaborateInfo)
  const currentPageUrl = useSelector(getCurrentPageUrlFilter)
  const [getCount, { data: countValue, isSuccess }] = useGetTaskCountMutation();
  const [approvePage] = useApprovePageMutation()
  const dispatch = useDispatch()
  const browseMode = useSelector(getBrowseMode);
  const { isMobile } = useResponsive();
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);
  const handleGodaddyDoneRedirect = () => {
    getCount({ siteId: siteId ?? '', status: showTaskCountModal })
    dispatch(setShowTaskCountModal(true))
  };
  const handleGodaddyCreateRedline = (_value: PageStatusType) => {
    if (!siteId && !currentPageUrl) return
    approvePage(
      {
        page_url: currentPageUrl ?? '',
        set_tasks_complete: false,
        site_id: siteId ?? '',
        godaddy: true,
      }
    )
  };


  return (
    <>
      {isBottomBarLoading ? (
        <Skeleton className="h-9 w-9 rounded-md" />
      ) : (
        <Button
          className={cn('ml-2 w-9 md:w-fit px-1 md:px-2 gap-0')}
          variant="muted"
          size={!browseMode ? "sm" : "icon"}
          leftIcon={<Save size={18} />}
          onClick={handleGodaddyDoneRedirect}
          onTouchEnd={handleGodaddyDoneRedirect}
          tooltipSide="top"
          tooltip={t('bottom_bar.enterprise.done_tooltip', 'Finished adding comments and feedback? Click here to return to your Dashboard')}
        >
          {(!browseMode && !isMobile) && (
            <motion.span
              initial={{ width: 0, opacity: 0, marginLeft: 0 }}
              animate={{ width: 'auto', opacity: 1, marginLeft: 6 }}
              exit={{ width: 0, opacity: 0, marginLeft: 0 }}
              transition={{ duration: 0.4, delay: 0.75, type: 'spring' }}
            >
              {t('bottom_bar.enterprise.save', 'Return To Dashboard')}
            </motion.span>
          )}
        </Button>
      )}

      {(itemDetails?.result?.role === 'admin' ||
        itemDetails?.result?.role === 'contributor') && (
          <>
            {isBottomBarLoading ? (
              <Skeleton className="h-9 w-9 rounded-md" />
            ) : (
              <Button
                className="ml-2"
                size="icon"
                variant="outline"
                onClick={() => handleGodaddyCreateRedline('approved')}
                onTouchEnd={() => handleGodaddyCreateRedline('approved')}
                tooltipSide="top"
                tooltip={t(
                  'bottom_bar.enterprise.create_redline_tooltip',
                  'Click this when you have completed all of your tasks on this website to create a red line with the date & time inside the sidebar'
                )}
              >
                <Check size={30} color={'#8791B2'} />
              </Button>
            )}
          </>
        )}

      {isSuccess && <GodaddyConfirmTaskModal countValue={countValue?.result} />}
    </>
  );
}
